import { useCallback, useEffect, useRef, useState } from 'react';
import { PlanDeTravail } from './plan-travail';
import { MesAffaires } from './mes-affaires';
import { appeler, ErreurApi } from './api';
/**
 * ⚠️ LE LIBELLÉ VIENT DU CATALOGUE PARTAGÉ, jamais d'un `replace('_',' ')` local.
 * L'écran affichait « Inscription_surete » — un mot de base de données sur un
 * poste d'État. `libelleTypeMutation` existe depuis longtemps et fait autorité :
 * une seconde table des mêmes libellés divergerait au premier type ajouté.
 */
import { libelleTypeMutation } from '../../src/partage/libelles';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LE BUREAU DU NOTAIRE — §3 · §4 · §51 · §68 · §83
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Fondateur : « Le notaire arrive sur un cockpit, pas sur une liste de
 * formulaires. »
 *
 * ⚠️ CE QUI SÉPARE UN COCKPIT D'UNE LISTE N'EST PAS LA MISE EN PAGE. Une liste
 * répond « voici des lignes ». Un cockpit répond aux quatre questions du §83 :
 * OÙ SUIS-JE · QUEL EST L'ÉTAT · QU'EST-CE QUI BLOQUE · QUELLE EST LA PROCHAINE
 * ACTION. La quatrième est la seule qui fasse travailler, et c'est celle
 * qu'aucun écran ne portait : la liste des mutations servait
 * `Référence | Type | Parcelle | Statut | Approb. | Déposée` — six colonnes qui
 * décrivent des lignes de table, et pas un seul geste.
 *
 * ⚠️ L'ÉCRAN NE DÉCIDE RIEN. La prochaine étape, son urgence et son POURQUOI
 * viennent de `fon.prochaine_etape` (migration 169), calculés au même endroit
 * pour la liste, la fiche et ce bureau. Un écran qui trancherait lui-même
 * afficherait tôt ou tard deux conseils contradictoires sur le même acte.
 *
 * ⚠️ L'ORDRE N'EST PAS CHRONOLOGIQUE. Ce qui BLOQUE passe devant. Trier par
 * date mettrait un acte terminé au-dessus d'un acte empêché, et l'agent
 * travaillerait dans le désordre de l'horloge.
 */

interface Compteur { valeur: number; sur: number | null; libelle: string; alerte?: number }
interface Acte {
  reference: string; type: string; statut: string; parcelle: string | null;
  affaire: string | null; etape: string; etape_libelle: string;
  pourquoi: string; urgence: string; bloquants: number; approbations: number;
}
interface Intention { cle: string; libelle: string; ouverte: boolean; motif: string | null }
interface Bureau {
  agent: { nom: string; portee: string };
  compteurs: Record<string, Compteur>;
  alertes: number;
  actes: Acte[];
  resume: { bloquants: number; a_faire: number; lecture: string };
  intentions: Intention[];
}

/**
 * ⚠️ LA PASTILLE PORTE UN MOT, PAS SEULEMENT UNE COULEUR. Ces écrans
 * s'impriment et se photographient ; et une part des agents distingue mal le
 * rouge de l'ambre. La règle est celle des documents d'État de ce dépôt :
 * jamais la seule teinte.
 */
const URGENCE: Record<string, { classe: string; mot: string }> = {
  bloquant: { classe: 'rouge', mot: 'empêché' },
  action:   { classe: 'ambre', mot: 'à faire' },
  attente:  { classe: 'bleu',  mot: 'chez un tiers' },
  rien:     { classe: 'vert',  mot: 'terminé' },
};

const FAMILLE: Record<string, string> = {
  personnes: 'Personnes', parcelles: 'Parcelles', affaires: 'Dossiers',
  actes: 'Actes', documents: 'Documents',
};

export function Bureau({ modeCode, onOuvrirActe, onOuvrirParcelle, onOuvrirOperation, onOuvrirRubrique, onOuvrirAffaire, onReprendreSaisie, onOuvrirDocument, onOuvrirPersonne }: {
  /** Le mode métier courant — le plan de travail est mesuré POUR lui. */
  modeCode: string;
  onOuvrirActe: (reference: string) => void;
  /**
   * ⚠️ OUVRIR UNE AFFAIRE EST UN GESTE À PART, et pas `onOuvrirRubrique('affaires')`.
   * La rubrique ouvre la LISTE ; ce geste ouvre CETTE affaire. Les confondre
   * rendrait le clic « presque » juste — l'agent arrive sur une liste où il
   * doit re-chercher ce qu'il venait d'ouvrir.
   */
  onOuvrirAffaire: (reference: string) => void;
  onReprendreSaisie?: (typeCode: string, parcelle: string | null) => void;
  onOuvrirDocument: (identifiant: string) => void;
  onOuvrirPersonne: (titulaireId: string) => void;
  /* ⚠️ `onOuvrirRubrique` A ÉTÉ RETIRÉ PUIS REMIS, et les deux gestes étaient
     justes. Retiré parce qu'il ne servait qu'à replier six intentions sur une
     seule destination — le défaut que le fondateur a vu. Remis parce que la
     fusion du plan de travail lui donne un emploi RÉEL : les tuiles du plan
     ouvrent leur rubrique. Un prop n'est ni bon ni mauvais en soi ; ce qui
     compte est ce qu'on lui fait faire. */
  onOuvrirRubrique: (rubrique: string) => void;
  onOuvrirParcelle: (identifiant: string) => void;
  /** Ouvre l'OPÉRATION nommée, pas la page qui la contient. Voir `app.tsx`. */
  onOuvrirOperation: (cle: string) => void;
}) {
  const [b, setB] = useState<Bureau | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [res, setRes] = useState<any>(null);
  const [cherche, setCherche] = useState(false);
  /**
   * ⚠️ UN COCKPIT MONTRE CE QUI COMPTE MAINTENANT, PAS TOUT. Mesuré à l'écran :
   * quarante actes repoussaient « Ouvrir une opération » loin sous la ligne de
   * flottaison — c'est-à-dire que le geste d'entrée du §51 devenait invisible,
   * et le cockpit redevenait une liste. On montre les plus urgents, et l'on DIT
   * combien restent : cacher sans le dire ferait conclure qu'il n'y a que huit
   * actes.
   */
  const [tout, setTout] = useState(false);
  const minuteur = useRef<number | null>(null);

  useEffect(() => {
    let vivant = true;
    appeler<Bureau>('/bureau')
      .then((x) => { if (vivant) setB(x); })
      .catch((e) => { if (vivant) setErreur(e instanceof ErreurApi ? e.message : String(e)); });
    return () => { vivant = false; };
  }, []);

  /**
   * ⚠️ LA RECHERCHE ATTEND QUE L'ON S'ARRÊTE DE TAPER. Sans ce délai, « Camara »
   * lance six requêtes dont cinq sont jetées — et sur un registre national,
   * chacune balaie des index de personnes. Le champ n'a pas à coûter six fois
   * son résultat.
   */
  const chercher = useCallback((terme: string) => {
    setQ(terme);
    if (minuteur.current) window.clearTimeout(minuteur.current);
    if (terme.trim().length < 2) { setRes(null); return; }
    minuteur.current = window.setTimeout(async () => {
      setCherche(true);
      try { setRes(await appeler<any>(`/recherche?q=${encodeURIComponent(terme.trim())}`)); }
      catch (e) { setRes({ erreur: e instanceof ErreurApi ? e.message : String(e) }); }
      finally { setCherche(false); }
    }, 300);
  }, []);

  if (erreur) return <div className="a-batir"><h2>Bureau</h2><p className="erreur">{erreur}</p></div>;
  if (!b) return <div className="attente-plein">Ouverture du bureau…</div>;

  return (
    <div className="bureau">
      {/* Le FLUX : tout ce qui se lit. Le rail d'opérations lui est frère,
          jamais un élément de grille qui traverse ses rangées — c'est ce qui
          fabriquait une bande morte sous le titre. */}
      <div className="bureau-flux">
      {/* ── §4 — UN SEUL CHAMP ─────────────────────────────────────────── */}
      <section className="bureau-recherche">
        <input
          className="bureau-champ"
          value={q}
          onChange={(e) => chercher(e.target.value)}
          placeholder="Rechercher — personne, société, parcelle, acte, dossier, document…"
          aria-label="Recherche universelle"
        />
        {/**
          * ⚠️ UN SEUL CHAMP, ET C'EST LE §68 : « l'utilisateur ne doit pas avoir
          * à comprendre la base de données pour effectuer son travail ». Celui
          * qui tient « GN-PAR-000000004 » sur un papier n'a pas à deviner si
          * c'est une parcelle, un acte ou un document.
          */}
        {cherche && <p className="attente">Recherche…</p>}
        {res && !cherche && (
          <div className="bureau-resultats">
            {res.erreur && <p className="erreur">{res.erreur}</p>}
            {res.lecture && <p className="mention">{res.lecture}</p>}
            {res.familles && Object.entries(res.familles).map(([fam, liste]: [string, any]) =>
              liste.length === 0 ? null : (
                <div key={fam} className="bureau-famille">
                  <h5>{FAMILLE[fam] ?? fam}</h5>
                  <ul>
                    {liste.map((x: any, i: number) => (
                      <li key={i}>
                        {fam === 'parcelles' && (
                          <button className="lien" onClick={() => onOuvrirParcelle(x.identifiant)}>
                            <code>{x.identifiant}</code> · {x.surface_m2} m² · {x.droits} droit(s)
                          </button>
                        )}
                        {fam === 'actes' && (
                          <button className="lien" onClick={() => onOuvrirActe(x.reference)}>
                            <code>{x.reference}</code> · {x.type} · {x.statut}
                          </button>
                        )}
                        {/**
                          * ⚠️⚠️ ELLE ÉTAIT INERTE, ET LA FICHE EXISTAIT DÉJÀ.
                          * Mon premier passage a écrit ici « fiche personne à
                          * bâtir » en croyant qu'aucun écran ne prenait un
                          * titulaire pour sujet. C'était faux : l'onglet
                          * « titulaires » de `foncier.tsx` charge
                          * `api.foncier.titulaire(id)` et rend le patrimoine
                          * depuis toujours. Il ne manquait pas d'écran, il
                          * manquait un CHEMIN — et j'avais documenté une
                          * absence au lieu de la mesurer. D.965.
                          */}
                        {fam === 'personnes' && (
                          <button className="lien" onClick={() => onOuvrirPersonne(x.id)}>
                            <strong>{x.denomination}</strong> · {x.droits} droit(s)
                            {!x.identite_verifiee && <span className="mention"> · identité non vérifiée</span>}
                          </button>
                        )}
                        {/**
                          * ⚠️⚠️ ELLE ÉTAIT RENDUE EN `<span>` — DU TEXTE MORT.
                          * Mesuré le 24/09/2026 : `parcelles` et `actes`
                          * portaient un `<button onClick>`, `affaires`,
                          * `personnes` et `documents` non. Le service TROUVE
                          * bien l'affaire (`recherche.ts:97`) : on pouvait la
                          * voir et pas l'ouvrir. Trois familles sur cinq
                          * étaient inertes — dont celle dont le fondateur dit
                          * que « toute démarche est une affaire ».
                          */}
                        {fam === 'affaires' && (
                          <button className="lien" onClick={() => onOuvrirAffaire(x.reference)}>
                            <code>{x.reference}</code> · {x.requerant} · {x.etape_courante}
                            {!x.vivant && <span className="mention"> · clos</span>}
                          </button>
                        )}
                        {fam === 'documents' && (
                          <button className="lien" onClick={() => onOuvrirDocument(x.identifiant)}>
                            <code>{x.identifiant}</code> · {x.type_code} · {x.emetteur}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>
        )}
      </section>

      {/* ── §3 — LES COMPTEURS, AVEC LEUR POPULATION ───────────────────── */}
      <section className="bureau-compteurs">
        {Object.entries(b.compteurs).map(([cle, c]) => (
          <div key={cle} className={`bureau-compteur ${c.alerte ? 'alerte' : ''}`}>
            <span className="bureau-chiffre">{c.valeur}</span>
            {/**
              * ⚠️ « 3 SUR 10 », JAMAIS « 3 ». Règle du plan de travail : un
              * nombre nu laisse croire qu'un zéro est une bonne nouvelle, alors
              * qu'il peut vouloir dire « rien ne vous est confié ».
              */}
            {c.sur !== null && <span className="bureau-sur">sur {c.sur}</span>}
            <span className="bureau-libelle">{c.libelle}</span>
            {c.alerte ? <span className="etat rouge">{c.alerte} en retard</span> : null}
          </div>
        ))}
      </section>

      {/* ── LA PORTE VERS LES AFFAIRES ──────────────────────────────────
          ⚠️ AVANT « Mes actes », ET C'EST LA DÉCISION. « Toute démarche est une
          affaire » (fondateur) ; l'acte n'est qu'un moment de l'affaire. Et
          mesuré le 24/09/2026 : 52 affaires sur 93 ne portent AUCUNE mutation,
          donc plus de la moitié du travail était invisible sur un bureau bâti
          sur `fon.mutation`. */}
      <MesAffaires onOuvrirAffaire={onOuvrirAffaire} onReprendreSaisie={onReprendreSaisie} />

      {/* ── §83 — CE QUI BLOQUE, ET LA PROCHAINE ACTION ────────────────── */}
      <section className="bureau-actes">
        <header className="bureau-tete">
          <h3>Mes actes</h3>
          <p className={b.resume.bloquants > 0 ? 'erreur' : 'mention'}>{b.resume.lecture}</p>
        </header>

        {b.actes.length === 0
          ? <p className="mention">{b.resume.lecture}</p>
          : (
            <ul className="bureau-liste">
              {(tout ? b.actes : b.actes.slice(0, 8)).map((a) => {
                const u = URGENCE[a.urgence] ?? URGENCE.rien!;
                return (
                  <li key={a.reference} className={`bureau-acte ${u.classe}`}>
                    <button className="bureau-acte-ouvrir" onClick={() => onOuvrirActe(a.reference)}>
                      <span className={`bureau-pastille ${u.classe}`} aria-hidden="true" />
                      <span className="bureau-acte-titre">
                        <strong>{libelleTypeMutation(a.type)}</strong>
                        <code>{a.reference}</code>
                        {a.parcelle && <span className="mention">{a.parcelle}</span>}
                      </span>
                      <span className="bureau-acte-etape">
                        <strong>{a.etape_libelle}</strong>
                        {/**
                          * ⚠️ LE POURQUOI EST AFFICHÉ AVEC LE GESTE, jamais
                          * derrière un survol. Un ordre sans raison se
                          * discute ; une raison fait agir.
                          */}
                        <span className="mention">{a.pourquoi}</span>
                      </span>
                      <span className={`etat ${u.classe}`}>{u.mot}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        {b.actes.length > 8 && (
          <button className="bouton discret" onClick={() => setTout(!tout)}>
            {tout
              ? `Ne montrer que les 8 plus urgents`
              : `Voir les ${b.actes.length - 8} autres actes`}
          </button>
        )}
      </section>

      {/* ── LE PLAN DE TRAVAIL, FUSIONNÉ ICI ─────────────────────────────
          ⚠️ Décision du fondateur, 21/09/2026 : « bureau et plan de travail
          doivent être fusionnés ». Elle contredit ma propre migration 170, qui
          plaidait leur coexistence — l'arbitrage lui revient, et il est posé.

          ⚠️ MAIS LA FUSION EST LOCALE AU NOTAIRE, et ce n'est pas une nuance :
          `plan` est l'écran d'ATTERRISSAGE de six autres modes qui n'ont aucun
          bureau (collectivite, etat, fiscal, geometre, investisseur,
          urbanisme). Le supprimer les laisserait sans point d'arrivée. Ce qui
          disparaît, c'est l'entrée `plan` DU RAIL DU NOTAIRE — pas la rubrique.

          ⚠️ ET IL RÉPOND À UNE AUTRE QUESTION QUE LES COMPTEURS. Les compteurs
          disent « combien » ; le plan dit « quoi, et ai-je le droit de le
          savoir » — une tuile fermée par l'habilitation affiche un cadenas et
          son motif, jamais un 0. Les deux ne se remplacent pas. */}
      <PlanDeTravail modeCode={modeCode} onOuvrirRubrique={onOuvrirRubrique} encastre />

      {/* ── §51 · §68 — DES INTENTIONS, PAS DES FORMULAIRES ────────────── */}
      </div>

      <section className="bureau-intentions">
        <h3>Ouvrir une opération</h3>
        <div className="bureau-boutons">
          {b.intentions.map((i) => (
            <button
              key={i.cle}
              className="bouton discret"
              disabled={!i.ouverte}
              /* ⚠️ UN BOUTON FERMÉ EST AFFICHÉ ET MOTIVÉ, jamais absent :
                 absent, l'agent conclut que la fonction n'existe pas. */
              title={i.ouverte ? i.libelle : (i.motif ?? 'non accordé à vos rôles')}
              /* ⚠️ ON TRANSMET LA CLÉ, PAS UNE DESTINATION. Cette ligne
                 traduisait six intentions distinctes en une seule rubrique :
                 « Donation entre vifs » et « Duplicata de titre » menaient au
                 même endroit, et l'agent devait re-choisir son type. Le bouton
                 nommait une opération sans en ouvrir aucune. */
              onClick={() => onOuvrirOperation(i.cle)}
            >{i.libelle}
            </button>
          ))}
        </div>
        <p className="mention">
          Une opération part d’une intention, pas d’un formulaire : le registre vous demande
          ensuite ce qu’il lui faut, et refuse ce qu’il ne peut pas recevoir.
        </p>
      </section>
    </div>
  );
}
